import type { Theme } from '../generated/gql-types';

/** Fallbacks when theme is not loaded (matches previous indigo palette). */
export const ACTION_THEME_FALLBACKS = {
  actionColor: '#6366f1',
  actionColor10: '#eef2ff',
  actionColor20: '#e0e7ff',
  actionColor60: '#4f46e5',
  actionColor100: '#312e81',
} as const;

export type ResolvedActionTheme = {
  actionColor: string;
  actionColor10: string;
  actionColor20: string;
  actionColor60: string;
  actionColor100: string;
};

export const resolveActionTheme = (
  theme: Theme | null,
): ResolvedActionTheme => ({
  actionColor: theme?.actionColor ?? ACTION_THEME_FALLBACKS.actionColor,
  actionColor10: theme?.actionColor10 ?? ACTION_THEME_FALLBACKS.actionColor10,
  actionColor20: theme?.actionColor20 ?? ACTION_THEME_FALLBACKS.actionColor20,
  actionColor60: theme?.actionColor60 ?? ACTION_THEME_FALLBACKS.actionColor60,
  actionColor100:
    theme?.actionColor100 ?? ACTION_THEME_FALLBACKS.actionColor100,
});

const CSS_VAR_NAMES = [
  '--action-color',
  '--action-color-10',
  '--action-color-20',
  '--action-color-60',
  '--action-color-100',
  '--action-color-hover',
] as const;

export const applyActionThemeToElement = (
  element: HTMLElement,
  theme: Theme | null,
): void => {
  const colors = resolveActionTheme(theme);
  element.style.setProperty('--action-color', colors.actionColor);
  element.style.setProperty('--action-color-10', colors.actionColor10);
  element.style.setProperty('--action-color-20', colors.actionColor20);
  element.style.setProperty('--action-color-60', colors.actionColor60);
  element.style.setProperty('--action-color-100', colors.actionColor100);
  element.style.setProperty('--action-color-hover', colors.actionColor60);
};

export const clearActionThemeFromElement = (element: HTMLElement): void => {
  CSS_VAR_NAMES.forEach((name) => element.style.removeProperty(name));
};

/** Focus ring/border using tenant action colour. */
export const actionFocusRing1ClassName =
  'focus:border-[var(--action-color)] focus:ring-1 focus:ring-[var(--action-color)] focus:outline-hidden';

export const actionFocusRing2ClassName =
  'focus:border-[var(--action-color)] focus:ring-2 focus:ring-[var(--action-color)] focus:outline-hidden';

export const actionFocusRingClassName =
  'focus:border-[var(--action-color)] focus:ring-[var(--action-color)] focus:outline-hidden';

export const actionLinkClassName =
  'text-[var(--action-color)] hover:text-[var(--action-color-hover)]';

export const actionTextClassName = 'text-[var(--action-color)]';

export const actionBgClassName = 'bg-[var(--action-color)]';

export const actionBgHoverClassName =
  'bg-[var(--action-color)] hover:bg-[var(--action-color-hover)]';

export const actionPrimaryButtonClassName =
  'bg-[var(--action-color)] text-white hover:bg-[var(--action-color-hover)] focus:ring-2 focus:ring-[var(--action-color)] focus:ring-offset-2 focus:outline-hidden';

export const actionListboxOptionActiveClassName =
  'bg-[var(--action-color-10)] text-[var(--action-color-100)]';

export const actionListboxButtonClassName = `relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-sm ${actionFocusRing1ClassName}`;

export const actionListboxButtonMdClassName = `relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left text-sm shadow-sm ${actionFocusRing1ClassName}`;

export const actionChipClassName =
  'bg-[var(--action-color-10)] text-[var(--action-color)] hover:bg-[var(--action-color-20)]';

/** Common auth / form text input with themed focus. */
export const actionFormInputClassName = `mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 sm:text-sm ${actionFocusRing2ClassName}`;

/** Worker details / modal text field with themed focus. */
export const actionFieldInputClassName = `rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 ${actionFocusRingClassName}`;

export const actionFieldInputBlockClassName = `mt-1 block w-full ${actionFieldInputClassName}`;

export const actionModalInputClassName = `w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs ${actionFocusRing2ClassName}`;

export const actionAuthSubmitButtonClassName = `flex w-full cursor-pointer justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-xs ${actionPrimaryButtonClassName}`;
